import React from 'react'
import { Trash2, Edit, Eye, ArrowUp, ArrowDown, Settings } from 'lucide-react'
import { getImageUrl } from '../../utils/imageUtils'

const SlideCard = ({ 
  slide, 
  index, 
  totalSlides,
  isSelected,
  isDeleting = false,
  onSelect,
  onEdit,
  onDelete,
  onToggleActive,
  onMoveUp,
  onMoveDown
}) => {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative ${isDeleting ? 'opacity-50' : ''}`}>
      {isDeleting && (
        <div className="absolute inset-0 bg-white/90 flex items-center justify-center z-10 rounded-xl">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            <span className="text-sm text-gray-600 font-medium">Deleting...</span>
          </div>
        </div>
      )}
      <div className="flex">
        {/* Image Preview */}
        <div className="w-48 h-32 bg-gray-100 flex-shrink-0 relative">
          {slide.image ? (
            <img
              src={getImageUrl(slide.image)}
              alt={slide.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gray-200"><svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>'
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onSelect}
                    disabled={isDeleting}
                    className="rounded border-gray-300 text-primary-purple focus:ring-primary-purple disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>
                <h3 className="text-lg font-semibold text-gray-900">{slide.title}</h3>
                <span className="text-sm text-gray-500">Order: {slide.order}</span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  slide.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {slide.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <p className="text-gray-600 mb-2 line-clamp-2">{slide.caption}</p>
              
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>Button: {slide.buttonText || 'None'}</span>
                <span>Link: {slide.buttonLink || 'None'}</span>
                <span>Uploaded: {slide.uploadDate || new Date(slide.createdAt).toLocaleDateString()}</span>
                <span>By: {slide.createdBy?.name || slide.createdBy?.username || 'Unknown'}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={onMoveUp}
                disabled={index === 0 || isDeleting}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed p-2 hover:bg-gray-50 rounded"
                title="Move up"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                onClick={onMoveDown}
                disabled={index === totalSlides - 1 || isDeleting}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed p-2 hover:bg-gray-50 rounded"
                title="Move down"
              >
                <ArrowDown className="w-4 h-4" />
              </button>
              <button
                onClick={onToggleActive}
                disabled={isDeleting}
                className={`p-2 rounded ${slide.isActive ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'} disabled:opacity-50 disabled:cursor-not-allowed`}
                title={slide.isActive ? 'Deactivate' : 'Activate'}
              >
                <Settings className="w-4 h-4" />
              </button>
              <button
                onClick={onEdit}
                disabled={isDeleting}
                className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Edit"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={onDelete}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SlideCard